/**
 * The file library.
 *
 * Three decisions here are not obvious from the screen:
 *
 * **The download is a link, not a fetch.** The response carries
 * `Content-Disposition: attachment`, so navigating to it is what makes the
 * browser save the file. Fetching it into memory first would also work, and
 * would hold an arbitrarily large file in the tab for no reason.
 *
 * **The size limit is checked here as well as on the server.** Not instead —
 * the server checks the bytes it actually received, which is the check that
 * counts. This one exists so that a 200 MB file is refused before it is
 * uploaded rather than after, which is the difference between an instant "too
 * large" and two minutes of progress bar ending in one.
 *
 * **The listing is forward-only.** It is paged by cursor, and the cursor is
 * the UUIDv7 primary key, which is time-ordered. There is no "page 7" to jump
 * to and the interface does not pretend otherwise.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import type { ApiClient } from "@hardauto/core-api";
import { Button, DataTable, Dialog, Page, useToast, type Column } from "@hardauto/core-ui";

import { FilesApi, type StoredFile } from "../api";
import { NAMESPACE } from "../i18n";
import "./files.css";

const PAGE_SIZE = 50;

/** i18next's `t`, as this file receives it. */
type Translate = ReturnType<typeof useTranslation>["t"];

/**
 * Human sizes.
 *
 * The unit is a translation key rather than a suffix concatenated here: "kB"
 * is "ko" in French and "ك.بايت" in Arabic, and a hard-coded suffix is exactly
 * the kind of string that survives every review because it does not look like
 * prose.
 */
function formatSize(bytes: number, t: Translate): string {
  if (bytes < 1024) return t("unit.b", { count: bytes });
  if (bytes < 1024 * 1024) return t("unit.kb", { count: Math.round(bytes / 1024) });
  return t("unit.mb", { count: Math.round((bytes / (1024 * 1024)) * 10) / 10 });
}

export interface FilesScreenProps {
  client: ApiClient;
  /**
   * The server's limit, so the refusal can happen before the upload.
   *
   * Supplied by the application rather than read from a settings endpoint:
   * this package should not need a second round trip to render, and the value
   * changes only with a deployment.
   */
  maxUploadBytes?: number | undefined;
}

export function FilesScreen({ client, maxUploadBytes = 10 * 1024 * 1024 }: FilesScreenProps) {
  const { t } = useTranslation(NAMESPACE);
  const api = useMemo(() => new FilesApi(client), [client]);
  const toast = useToast();
  const input = useRef<HTMLInputElement>(null);

  const [files, setFiles] = useState<StoredFile[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<StoredFile | null>(null);

  const load = useCallback(
    async (after?: string) => {
      setLoading(true);
      setFailed(false);
      try {
        const page = await api.list({ after_id: after, limit: PAGE_SIZE });
        // Appended when paging, replaced when loading afresh — so "show more"
        // adds to the list rather than replacing what the user was reading.
        setFiles((current) => (after ? [...current, ...page.files] : page.files));
        setCursor(page.next_cursor);
      } catch {
        setFailed(true);
      } finally {
        setLoading(false);
      }
    },
    [api],
  );

  useEffect(() => {
    void load();
  }, [load]);

  const onPick = useCallback(
    async (picked: File | undefined) => {
      if (!picked) return;

      if (picked.size > maxUploadBytes) {
        toast.show(
          t("upload.tooLarge", {
            name: picked.name,
            limit: formatSize(maxUploadBytes, t),
          }),
          { tone: "warning" },
        );
        return;
      }

      setBusy(true);
      try {
        await api.upload(picked);
        toast.show(t("upload.done", { name: picked.name }), { tone: "success" });
        await load();
      } catch (error) {
        // The server's refusal is more useful than ours: it knows whether the
        // type was disallowed, the quota was reached, or the capability is
        // switched off for this tenant. Shown when it says something.
        const reason = error instanceof Error ? error.message : "";
        toast.show(
          reason
            ? t("upload.rejected", { name: picked.name, reason })
            : t("upload.failed", { name: picked.name }),
          { tone: "danger" },
        );
      } finally {
        setBusy(false);
        // Cleared so picking the same file twice fires `change` the second
        // time. Without this, a failed upload cannot be retried by choosing
        // the same file again, which is exactly what a user would try.
        if (input.current) input.current.value = "";
      }
    },
    [api, load, maxUploadBytes, t, toast],
  );

  const confirmDelete = useCallback(async () => {
    const target = pendingDelete;
    if (!target) return;
    setPendingDelete(null);
    try {
      await api.remove(target.id);
      setFiles((current) => current.filter((file) => file.id !== target.id));
      toast.show(t("deleted", { name: target.filename }), { tone: "success" });
    } catch {
      toast.show(t("deleteFailed", { name: target.filename }), { tone: "danger" });
    }
  }, [api, pendingDelete, t, toast]);

  const columns: Column<StoredFile>[] = [
    { key: "name", header: t("column.name"), render: (file) => file.filename },
    {
      key: "type",
      header: t("column.type"),
      render: (file) => <code>{file.content_type}</code>,
    },
    { key: "size", header: t("column.size"), render: (file) => formatSize(file.size, t) },
    {
      key: "added",
      header: t("column.added"),
      render: (file) => (file.created_at ? new Date(file.created_at).toLocaleString() : "—"),
    },
    {
      key: "actions",
      header: t("column.actions"),
      render: (file) => (
        <div className="hardauto-file__actions">
          {/* An anchor, not a button with an onClick: the response is an
              attachment, and letting the browser navigate is what saves it. */}
          <a
            className="hardauto-file__download"
            href={api.downloadPath(file.id)}
            aria-label={t("action.downloadFile", { name: file.filename })}
          >
            {t("action.download")}
          </a>
          <Button
            size="sm"
            variant="ghost"
            aria-label={t("action.deleteFile", { name: file.filename })}
            onClick={() => {
              setPendingDelete(file);
            }}
          >
            {t("action.delete")}
          </Button>
        </div>
      ),
    },
  ];

  return (
    <Page
      title={t("title")}
      description={t("intro")}
      actions={
        <>
          <input
            ref={input}
            type="file"
            hidden
            onChange={(event) => {
              void onPick(event.target.files?.[0]);
            }}
          />
          <Button
            disabled={busy}
            onClick={() => {
              input.current?.click();
            }}
          >
            {t("upload.choose")}
          </Button>
        </>
      }
    >
      <DataTable
        caption={t("title")}
        columns={columns}
        rows={files}
        rowKey={(file) => file.id}
        loading={loading && files.length === 0}
        loadingLabel={t("loading")}
        error={
          failed
            ? {
                title: t("error.load"),
                onRetry: () => {
                  void load();
                },
                retryLabel: t("error.retry"),
              }
            : undefined
        }
        empty={{ title: t("empty.title"), description: t("empty.body") }}
      />

      {cursor ? (
        <Button
          variant="ghost"
          disabled={loading}
          onClick={() => {
            void load(cursor);
          }}
        >
          {t("action.more")}
        </Button>
      ) : null}

      {/* A confirmation, because deleting removes the content from storage and
          there is nothing to restore it from. */}
      <Dialog
        open={pendingDelete !== null}
        title={t("action.confirmTitle", { name: pendingDelete?.filename ?? "" })}
        // Distinct from "Cancel": the dialog's own dismiss control and the
        // footer button do the same thing, and giving them one name leaves a
        // screen-reader user choosing between two identical buttons.
        closeLabel={t("action.close")}
        onClose={() => {
          setPendingDelete(null);
        }}
        footer={
          <>
            <Button
              variant="ghost"
              onClick={() => {
                setPendingDelete(null);
              }}
            >
              {t("action.cancel")}
            </Button>
            <Button
              variant="danger"
              onClick={() => {
                void confirmDelete();
              }}
            >
              {t("action.confirm")}
            </Button>
          </>
        }
      >
        {t("action.confirmBody")}
      </Dialog>
    </Page>
  );
}
